import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, Alert, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import Spinner from '../../components/Spinner';

// Rule-based syllable count estimator for dynamic user input
const countSyllables = (word: string): number => {
  let w = word.toLowerCase().trim().replace(/[^a-z]/g, '');
  if (!w) return 0;
  if (w.length <= 3) return 1;
  
  // Remove quiet/silent 'e' at the end, but keep '-le'
  if (w.endsWith('e') && !w.endsWith('le')) {
    w = w.slice(0, -1);
  }
  
  // Match vowel combinations
  const matches = w.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

// Heuristic to split a word into rough syllables (interactive/educational visual aid)
const splitSyllables = (word: string): string[] => {
  const w = word.toLowerCase().trim().replace(/[^a-z]/g, '');
  if (!w) return [];
  if (w.length <= 3) return [word];

  const syllables: string[] = [];
  let temp = '';
  const vowels = 'aeiouy';

  for (let i = 0; i < w.length; i++) {
    const char = w[i];
    temp += char;

    // Split heuristic: vowel followed by two consonants (VCCV) -> split between consonants
    if (vowels.includes(char) && i + 2 < w.length) {
      const next1 = w[i + 1];
      const next2 = w[i + 2];
      if (!vowels.includes(next1) && !vowels.includes(next2)) {
        temp += next1;
        syllables.push(temp);
        temp = '';
        i++; // skip next1
      } else if (!vowels.includes(next1) && vowels.includes(next2)) {
        // VCV -> split before the consonant
        syllables.push(temp);
        temp = '';
      }
    }
  }
  if (temp) {
    syllables.push(temp);
  }

  // Capitalize first letter of the first syllable for aesthetics
  if (syllables.length > 0) {
    const originalInput = word.trim();
    let currentIdx = 0;
    const reconstructed = syllables.map((syl) => {
      const part = originalInput.substring(currentIdx, currentIdx + syl.length);
      currentIdx += syl.length;
      return part;
    });
    return reconstructed.filter(Boolean);
  }

  return [word];
};

const Syllabification: React.FC = () => {
  const navigate = useNavigate();
  const [examples, setExamples] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dynamic tool state
  const [inputWord, setInputWord] = useState('');
  const [analyzedResult, setAnalyzedResult] = useState<{ word: string; parts: string[]; beats: number } | null>(null);

  const fetchExamples = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/language-hub?type=syllabification`);
      if (res.data && res.data.success) {
        setExamples(res.data.data);
      } else {
        setError('Failed to fetch syllabification examples.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to syllabification API');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExamples();
  }, []);

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputWord.trim()) return;

    // Check if the word exists in our seed/examples first to give the exact database split if available
    const exactMatch = examples.find(ex => ex.word.toLowerCase() === inputWord.trim().toLowerCase());
    if (exactMatch) {
      setAnalyzedResult({
        word: exactMatch.word,
        parts: exactMatch.parts,
        beats: exactMatch.beats
      });
    } else {
      // Dynamic fallback
      const parts = splitSyllables(inputWord);
      const beats = countSyllables(inputWord);
      setAnalyzedResult({
        word: inputWord.trim(),
        parts,
        beats
      });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Syllabification & Sound Beats
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Understand word parts and rhythmic speech. Type any word below to count its syllables and inspect its sound beats.
        </Typography>
      </Box>

      {/* Interactive Analyzer Tool */}
      <Card 
        sx={{ 
          p: 4, 
          borderRadius: 4, 
          mb: 6, 
          background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.04) 0%, rgba(244, 63, 94, 0.01) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.08)' 
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, color: '#fb7185' }}>
            <QueueMusicIcon />
            Interactive Syllable Parser
          </Typography>

          <form onSubmit={handleAnalyze}>
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter any English word (e.g. Incomprehensible)..."
                value={inputWord}
                onChange={(e) => setInputWord(e.target.value)}
              />
              <Button type="submit" variant="contained" color="error" sx={{ px: 4 }}>
                Analyze
              </Button>
            </Box>
          </form>

          {analyzedResult && (
            <Box sx={{ p: 3, borderRadius: 3, backgroundColor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Analysis for: {analyzedResult.word}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Syllables:</Typography>
                {analyzedResult.parts.map((part, idx) => (
                  <Box 
                    key={idx} 
                    sx={{ 
                      px: 2, 
                      py: 1, 
                      borderRadius: 2, 
                      backgroundColor: 'rgba(244, 63, 94, 0.15)', 
                      border: '1px solid rgba(244, 63, 94, 0.3)',
                      color: '#fda4af',
                      fontWeight: 700,
                      fontSize: '1.1rem'
                    }}
                  >
                    {part}
                  </Box>
                ))}
              </Box>

              <Typography variant="body1">
                <strong>Estimated Sound Beats:</strong> {analyzedResult.beats}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ my: 5 }} />

      {/* Examples section */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
          Seeded Examples & Definitions
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Spinner />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {examples.map((item) => (
              <Grid item xs={12} sm={6} key={item._id}>
                <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.05)', background: 'transparent' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.word}</Typography>
                      <Button variant="outlined" color="error" size="small">
                        {item.beats} Beats
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {item.parts.map((p: string, idx: number) => (
                        <Typography key={idx} sx={{ px: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, fontSize: '0.9rem' }}>
                          {p}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.definition}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default Syllabification;
